// @ts-ignore
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Layout } from 'components/Layout/Layout';
import Head from 'next/head';
import { useRouter } from 'next/router';
import styled, { css } from 'styled-components';
import { Header } from 'components/Layout/Header';
import { ActionButton } from '../components/Buttons/ActionButton';
import { Footer } from '../components/Layout/Footer';
import StoryblokService from '../utils/storyblok-service';
import { POSTS_PER_PAGE_IN_REQUEST } from '../consts';
import { ListCourses } from '../components/CourseContent/ListCourses';
import { useMediaQuery } from 'react-responsive';
import { Consts as StyleConsts } from '../styles/consts';
import { Text } from 'components/Typography/Typography';

export async function getStaticProps({ query }) {
  StoryblokService.setQuery(query);

  const stories = await StoryblokService.get('cdn/stories', {
    starts_with: 'course/',
    is_startpage: 0,
    per_page: POSTS_PER_PAGE_IN_REQUEST,
  });

  return {
    props: {
      stories: stories.data.stories,
    },
  };
}

const LearningContent = ({ stories }) => {
  const router = useRouter();
  const [isMobile, setMobile] = useState(false);
  const mobile = useMediaQuery({
    query: `(max-width: ${StyleConsts.mobileBreakPoint})`,
  });

  useEffect(() => {
    setMobile(mobile);
  }, [mobile]);
  return (
    <Layout id="layout">
      <Head>
        <title>The Earth Prize - Learning Content</title>
        <meta
          name="description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta
          property="og:title"
          content="The Earth Prize - Learning Content"
        />
        <meta
          property="og:description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:image" content="/images/cover.jpg" />
      </Head>
      <Header />
      <LearningContentWrapper>
        <HeroWrapper>
          <Overlay />
          <Title>
            {' '}
            The Earth Prize
            <br />
            Learning Content
          </Title>

          <WrapperSecondaryTitle>
            <SecondaryTitle>
              Learn more about the Earth, hear inspiring stories from young and
              successful environmental changemakers, and listen to useful tips
              and tricks on how to become a change-maker yourself! Register for
              free to The Earth Prize competition to get full access to our
              content.
            </SecondaryTitle>
          </WrapperSecondaryTitle>
        </HeroWrapper>
      </LearningContentWrapper>
      <ListCourses data={stories} learningContent />
      <LastSection>
        <WrapperText>
          <H3>Want to see more?</H3>
          <Text>
            Let us know which young change-maker you would like to see featured
            next on The Earth Prize Learning Content!
          </Text>
        </WrapperText>
        <ActionButton
          background="#00B7BC"
          color="black"
          onClick={() => router.push('/about-us#contact-us')}
        >
          SUGGEST A CHANGE-MAKER
        </ActionButton>
      </LastSection>
      <Footer />
    </Layout>
  );
};

const LearningContentWrapper = styled.div`
  background-color: black;
  position: relative;
  color: white;
`;

const HeroWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    background-image: url('/images/earth_back_2.webp');
    background-size: cover;
    background-position: center top;
    background-repeat: no-repeat;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    height: 100vh;
    padding: 0 12.15vw 6.944vw;
    color: white;
    gap: 40px;
    position: relative;
    @media (max-width: ${Consts.mobileBreakPoint}) {
      background-image: url('/images/earth_back_Mobile.webp');
      gap: 32px;
      padding: 0 20px 75px;
    }
  `}
`;
const Overlay = styled.div`
  ${({ theme: { Consts } }) => css`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: linear-gradient(
      2.16deg,
      #000000 2.41%,
      rgba(0, 0, 0, 0) 34.1%,
      rgba(0, 0, 0, 0.092487) 89.78%,
      rgba(0, 0, 0, 0.34) 126.45%
    );
    @media (max-width: ${Consts.mobileBreakPoint}) {
      background: linear-gradient(
        1.26deg,
        #000000 -7%,
        rgba(0, 0, 0, 0) 26.48%,
        rgba(0, 0, 0, 0.092487) 87.47%,
        rgba(0, 0, 0, 0.34) 127.64%
      );
    }
  `}
`;
const WrapperSecondaryTitle = styled.div`
  ${({ theme: { Consts } }) => css`
    display: flex;
    justify-content: space-between;
    z-index: 1;
    button {
      align-self: flex-end;
    }
    @media (max-width: ${Consts.mobileBreakPoint}) {
      flex-direction: column;
      gap: 40px;
      button {
        font-size: 16px;
        line-height: 25px;
        align-self: center;
      }
    }
  `}
`;

const LastSection = styled.div`
  ${({ theme: { Consts } }) => css`
    display: flex;
    flex-direction: column;
    padding: 168px 0 191px;
    gap: 80px;
    align-items: center;
    @media (max-width: ${Consts.mobileBreakPoint}) {
      padding: 90px 35px 120px;
      gap: 40px;

      button {
        font-weight: bold;
        font-size: 16px;
        line-height: 25px;
      }
    }
  `}
`;
const WrapperText = styled.h3`
  ${({ theme: { Consts } }) => css`
    display: flex;
    flex-direction: column;
    font-weight: 700;
    font-size: 50px;
    gap: 20px;
    max-width: 555px;
    text-align: center;

    > p {
      max-width: 750px;
      text-align: center;
      margin: 0 auto 50px;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 36px;
      gap: 0;
      max-width: initial;
    }
  `}
`;

const Title = styled.h1`
  ${({ theme: { Consts } }) => css`
    font-weight: 700;
    font-size: 52px;
    line-height: 64px;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 36px;
      line-height: 41px;
    }
  `}
`;

const SecondaryTitle = styled.h2`
  ${({ theme: { Consts } }) => css`
    font-weight: normal;
    font-size: 17px;
    line-height: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-width: 602px;

    span {
      font-weight: 400;
      line-height: 30px;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 16px;
      line-height: 26px;
    }
  `}
`;

const H3 = styled.h2`
  ${({ theme: { Consts } }) => css`
    font-weight: 900;
    font-size: 40px;
    line-height: 55px;
    margin: 0 0 40px;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 25px;
      line-height: 34px;
    }
  `}
`;

export default LearningContent;
